import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { loadYearComparison } from '../../../server/server'
import WellbeingComparisonWidget from '../counsellor/WellbeingComparisonWidget'
import YearAlertsWidget from './YearAlertsWidget'
import InfoButton from '../../common/InfoButton'
import AnalyticsLoadingOverlay from '../AnalyticsLoadingOverlay'
import YearComparisonSkeleton from '../skeleton/YearComparisonSkeleton'
import YearAlertsSkeleton from '../skeleton/YearAlertsSkeleton'

export default function YearComparisonWidget({ scope, daysFilter, alerts = false }) {
    const { t } = useTranslation()
    const requestKey = JSON.stringify([scope, daysFilter.startDate, daysFilter.endDate, alerts])
    const [result, setResult] = useState({ key: null, data: null, error: false })
    const data = result.key === requestKey ? result.data : null
    const error = result.key === requestKey && result.error
    useEffect(() => {
        let active = true
        loadYearComparison(scope, daysFilter.startDate, daysFilter.endDate, alerts).then((response) => {
            if (!active) return
            setResult({ key: requestKey, data: response.status === 200 ? response.data : null, error: response.status !== 200 })
        }).catch(() => {
            if (active) setResult({ key: requestKey, data: null, error: true })
        })
        return () => { active = false }
    }, [scope, daysFilter.startDate, daysFilter.endDate, alerts, requestKey])
    if (data) {
        if (alerts) return <YearAlertsWidget data={data} daysFilter={daysFilter} />
        const comparisonData = {
            school: data.benchmark,
            benchmarkLabel: 'Year average',
            years: data.classes,
        }
        return <WellbeingComparisonWidget comparisonData={comparisonData} daysFilter={daysFilter} analyticsType="year" />
    }

    return (
        <section aria-label={t(alerts ? 'Alerts for selected classes' : 'Wellbeing comparison for selected classes')} aria-busy={!error} className={`bg-white rounded-[24px] ${alerts ? 'p-5' : 'p-6'} mt-4`}>
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple">{t(alerts ? 'Alerts for selected classes' : 'Wellbeing comparison for selected classes')}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px]">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <InfoButton color="#5E639B" text={t(alerts ? 'Unresolved alerts for this year during the selected period.' : 'Compare classes against the full year. The year average stays fixed when classes are filtered.')} />
            </div>
            {error ? <p role="alert" className="mt-4 text-darkenPurple">{t('Unable to load year analytics')}</p> : (
                <div className="relative mt-4">
                    {alerts ? <YearAlertsSkeleton /> : <YearComparisonSkeleton classCount={Math.min(scope.classIds?.length ?? 3, 5)} />}
                    <AnalyticsLoadingOverlay />
                </div>
            )}
        </section>
    )
}
